import {
  Control,
  Controller,
  FieldErrors,
  FormState,
  UseFormClearErrors,
  UseFormGetValues,
  UseFormSetValue,
  UseFormTrigger,
  UseFormWatch,
} from 'react-hook-form';
import { policyActions } from '../../shared/policy-actions';
import {
  PolicyAction as PolicyActionSchema,
  PolicyFormValues,
} from './policy-dialog';
import { GroupHeader, GroupItems } from '../../shared/autocomplete-groups-ui';
import SharedActionAccordion from '../../shared/shared-action-accordion';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import PolicyAction from './policy-action';
import { FormSubSteps } from '../../shared/shared-form-stepper';
import { getSchemaKeys } from '../../shared/simple-form-page';
import { ZodObject, ZodRawShape } from 'zod';

import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import UnfoldLess from '@mui/icons-material/UnfoldLess';

interface StepTwoProps {
  control: Control<PolicyFormValues>;
  errors: FieldErrors<PolicyFormValues>;
  watch: UseFormWatch<PolicyFormValues>;
  setValue: UseFormSetValue<PolicyFormValues>;
  domains: string[];
  gPanelRoles: string[];
  trigger: UseFormTrigger<PolicyFormValues>;
  clearErrors: UseFormClearErrors<PolicyFormValues>;
  expandedIds: Set<string>;
  setExpandedIds: Dispatch<SetStateAction<Set<string>>>;
  formState: FormState<PolicyFormValues>;
  getValues: UseFormGetValues<PolicyFormValues>;
  directoryRoles: string[];
  setActionSubSteps: Dispatch<SetStateAction<FormSubSteps[]>>;
}

export default function StepTwo({
  control,
  watch,
  setValue,
  errors,
  domains,
  gPanelRoles,
  trigger,
  clearErrors,
  expandedIds,
  setExpandedIds,
  formState,
  getValues,
  directoryRoles,
  setActionSubSteps,
}: StepTwoProps) {
  const policyType = watch('policyType') || '';
  const actions = policyActions.filter((policy) =>
    policy.policyTypes.includes(policyType)
  );

  const formActions = watch('actions') || [];
  const actionData = watch(`actionData`);
  const [expandedAll, setExpandedAll] = useState(false);

  const handleExpandAll = () => {
    setExpandedIds(
      () =>
        new Set(
          actionData.map((a) => {
            return a.type;
          })
        )
    );
  };

  const handleCollapseAll = () => {
    setExpandedIds(() => new Set());
  };

  const removeAction = (action: string) => {
    const type = policyActions.find((o) => o.label === action)?.value;
    setValue(
      'actions',
      formActions.filter((a) => a !== action)
    );

    setValue(
      'actionData',
      actionData.filter((a) => a.type !== type)
    );

    setExpandedIds((prev) => {
      const copy = new Set(prev);
      copy.delete(action);
      return copy;
    });

    setActionSubSteps((prev) => prev.filter((value) => value.title !== action));
  };

  const toggleExpand = (id: string, isExpanded: boolean) => {
    setExpandedIds((prev) => {
      const copy = new Set(prev);
      if (isExpanded) {
        copy.add(id);
      } else {
        copy.delete(id);
      }
      return copy;
    });
  };

  function hasErrors(
    id: number,
    action: PolicyActionSchema,
    schema?: ZodObject<ZodRawShape>
  ) {
    if (!schema) return false;
    const actionError = errors?.actionData?.[id];
    const fields = getSchemaKeys(schema);

    const hasFieldError = actionError
      ? fields.some((key) => key in actionError)
      : false;
    const isInvalid = !schema.safeParse(action).success;
    return hasFieldError || isInvalid;
  }

  useEffect(() => {
    const nextSubSteps = actionData
      .map((x) => {
        const actionConfig = policyActions.find(
          (option) => option.value === x.type
        );
        if (!actionConfig) return null;

        let hasError = false;

        if (actionConfig.schema) {
          hasError = !actionConfig.schema.safeParse(x).success;
        }

        return {
          stepIndex: 'Actions',
          title: actionConfig.label,
          hasError,
        };
      })
      .filter(
        (x): x is { stepIndex: string; title: string; hasError: boolean } => !!x
      );

    setActionSubSteps((prev) => {
      const same =
        prev.length === nextSubSteps.length &&
        prev.every(
          (p, i) =>
            p.title === nextSubSteps[i].title &&
            p.hasError === nextSubSteps[i].hasError
        );

      return same ? prev : nextSubSteps;
    });
  }, [errors.actionData, actionData, setActionSubSteps]);

  return (
    <Stack gap={1} width={'100%'}>
      <Typography sx={{ mb: 1 }}>
        Select the actions you would like to occur when this policy runs.
      </Typography>
      <Box sx={{ width: '100%' }}>
        <Stack
          direction="row"
          gap={1}
          sx={{ width: '100%', alignItems: 'start' }}
        >
          <Controller
            name="actions"
            control={control}
            render={({ field }) => (
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Autocomplete
                  multiple
                  limitTags={2}
                  options={actions.sort((a, b) =>
                    a.groupType.localeCompare(b.groupType)
                  )}
                  getOptionLabel={(option) => option?.label ?? ''}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Action"
                      error={!!errors.actions}
                      helperText={errors?.actions?.message || ''}
                      autoFocus
                    />
                  )}
                  groupBy={(option) => option?.groupType ?? ''}
                  renderGroup={(params) => (
                    <li key={params.key}>
                      <GroupHeader>{params.group}</GroupHeader>
                      <GroupItems>{params.children}</GroupItems>
                    </li>
                  )}
                  filterSelectedOptions
                  autoComplete
                  autoHighlight
                  fullWidth
                  value={actionData
                    .map((a) =>
                      actions.find((option) => option.value === a.type)
                    )
                    .filter((a) => a)}
                  onChange={(_, data) => {
                    const newSelectedActionValues = data
                      .map((action) => action?.value)
                      .filter((v): v is string => !!v);

                    const newActionData = newSelectedActionValues.map(
                      (actionValue) => {
                        const existingActionConfig = actionData.find(
                          (a) => a.type === actionValue
                        );

                        if (existingActionConfig) {
                          return existingActionConfig;
                        } else {
                          if (actionValue === 'Change_Contact_Sharing') {
                            return {
                              type: actionValue,
                              contactSharing: 'false',
                            };
                          } else if (
                            actionValue === 'Add_Delegate_To_Account'
                          ) {
                            return {
                              type: actionValue,
                              addDelegateToAccount: [],
                              valueType: 'User',
                            };
                          } else if (
                            actionValue === 'Remove_Delegate_From_Account'
                          ) {
                            return {
                              type: actionValue,
                              removeDelegateFromAccount: [],
                              valueType: 'User',
                              removeAllDelegatesEnabled: false,
                            };
                          } else if (actionValue === 'Add_To_Shared_Drive') {
                            return {
                              type: actionValue,
                              driveName: [],
                              driveRole: [],
                              driveId: [],
                            };
                          } else if (actionValue === 'Change_IMAP') {
                            return { type: actionValue, gmailIMAP: 'false' };
                          } else if (actionValue === 'Change_POP') {
                            return {
                              type: actionValue,
                              gmailPOPEanbled: 'false',
                              gmailPOPEnabledFor: 'ALL_MAIL',
                              gmailPOPAction: 'KEEP',
                            };
                          } else if (actionValue === 'Change_cPanel_Role') {
                            return { type: actionValue, cPanelIsAdmin: '' };
                          } else if (actionValue === 'Change_gPanel_Role') {
                            return { type: actionValue, roleName: '' };
                          } else if (
                            actionValue === 'Reassign_Aliases' ||
                            actionValue === 'Set_Forwarding'
                          ) {
                            return { type: actionValue, destUser: '' };
                          } else if (actionValue === 'Duplicate_User_Groups') {
                            return { type: actionValue, srcUser: '' };
                          } else if (actionValue === 'Remove_From_Group') {
                            return {
                              type: actionValue,
                              groupEmail: [],
                              removeAllGroups: 'false',
                            };
                          } else if (
                            actionValue === 'Remove_Domain_Findable_Shares'
                          ) {
                            return {
                              type: actionValue,
                              removeSearchableShares: false,
                              removeDomainFindableShares: true,
                            };
                          } else if (actionValue === 'Remove_Specific_Shares') {
                            return { type: actionValue, externalshare: [] };
                          } else if (actionValue === 'Set_Organization') {
                            return { type: actionValue, orgUnit: '' };
                          } else if (
                            actionValue === 'Apply_Signature_Template'
                          ) {
                            return {
                              type: actionValue,
                              signatureTemplateId: 0,
                            };
                          } else if (actionValue === 'Create_Gmail_Label') {
                            return { type: actionValue, gmailLabel: [] };
                          } else if (actionValue === 'Suspend_Inactive_Users') {
                            return { type: actionValue, daysBack: 0 };
                          } else if (actionValue === 'Suspend_User') {
                            return { type: actionValue, resetDecom: 'false' };
                          } else if (actionValue === 'Add_To_Group') {
                            return {
                              type: actionValue,
                              groupEmail: [],
                              groupRole: [],
                            };
                          } else if (actionValue === 'Update_Mobile_Devices') {
                            return {
                              type: actionValue,
                              daysBack: 0,
                              remoteWipe: 'false',
                              remoteAccountWipe: 'false',
                              block: 'false',
                              delete: 'false',
                            };
                          } else if (actionValue === 'Add_Send_As') {
                            return {
                              type: actionValue,
                              domainList: [],
                              defaultSendAs: '',
                            };
                          } else if (actionValue === 'Create_Gmail_Filter') {
                            return {
                              type: actionValue,
                              gmailFilter: {
                                criteria: {
                                  from: '',
                                  to: '',
                                  subject: '',
                                  query: '',
                                  negatedQuery: '',
                                  hasAttachment: false,
                                  excludeChats: false,
                                  size: 0,
                                  sizeComparison: 'larger',
                                  sizeType: 'MB',
                                },
                                action: {
                                  forward: '',
                                  addLabelIds: [],
                                  removeLabelIds: [],
                                },
                              },
                            };
                          } else if (actionValue === 'Last_Login_Action') {
                            return {
                              type: actionValue,
                              lastLoginActionType: 'suspend',
                              daysNotLoggedIn: 7,
                            };
                          } else if (
                            actionValue === 'Set_Profile_Information'
                          ) {
                            return {
                              type: actionValue,
                              userModelTemplate: undefined,
                            };
                          } else if (
                            actionValue === 'Dynamic_Group_Assignment'
                          ) {
                            return {
                              type: actionValue,
                              dynamicGroupAssignment: {
                                profileFieldType: '',
                                profileField: '',
                                regEx: '',
                                exact: false,
                                groupEmail: [],
                                groupRole: [],
                              },
                            };
                          } else {
                            return { type: actionValue };
                          }
                        }
                      }
                    );

                    setValue(
                      'actionData',
                      newActionData as PolicyActionSchema[]
                    );
                    setActionSubSteps(
                      newActionData.map((x, id) => {
                        const config = policyActions.find(
                          (o) => o.value === x.type
                        );
                        return {
                          stepIndex: 'Actions',
                          title: config?.label ?? x.type,
                          hasError: hasErrors(
                            id,
                            actionData[id],
                            config?.schema as ZodObject<ZodRawShape>
                          ),
                        };
                      })
                    );
                    field.onChange(data.map((x) => x?.label ?? ''));
                    clearErrors();
                  }}
                  size="small"
                />
              </Box>
            )}
          />
          <Tooltip arrow title="Collapse/Expand All">
            <IconButton
              sx={{ mt: 0.5 }}
              onClick={() => {
                if (!expandedAll) {
                  handleExpandAll();
                  setExpandedAll(true);
                } else {
                  handleCollapseAll();
                  setExpandedAll(false);
                }
              }}
              size="small"
            >
              <UnfoldLess />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>
      <Box
        sx={{
          width: '100%',
          overflowX: 'auto',
          p: 0.5,
        }}
      >
        <Stack
          gap={2}
          flexDirection="column"
          sx={{
            width: '100%',
          }}
        >
          {formActions
            .map((action, idx) => ({ action, idx }))
            .reverse()
            .map(({ action, idx: originalIdx }) => {
              const selectedAction = policyActions.find(
                (policyAction) => policyAction.label === action
              );
              const currentAction = actionData.find(
                (policyAction) => policyAction.type === selectedAction?.value
              );

              if (!selectedAction || !currentAction) return null;
              return (
                <SharedActionAccordion
                  key={selectedAction.value}
                  id={selectedAction.value}
                  type={selectedAction.groupType}
                  title={selectedAction.label}
                  description={selectedAction.description}
                  expanded={expandedIds.has(selectedAction.value)}
                  onToggleExpand={(exp) =>
                    toggleExpand(selectedAction.value, exp)
                  }
                  onRemove={() => removeAction(selectedAction.label)}
                  hasNeedsAttention={hasErrors(
                    originalIdx,
                    currentAction,
                    selectedAction.schema as ZodObject<ZodRawShape>
                  )}
                >
                  {selectedAction.hasContent ? (
                    <PolicyAction
                      key={selectedAction.value}
                      control={control}
                      setValue={setValue}
                      watch={watch}
                      errors={errors}
                      actionType={selectedAction.value}
                      actionDisplayValue={action}
                      actionTypeDescription={selectedAction.value}
                      trigger={trigger}
                      domains={domains}
                      gPanelRoles={gPanelRoles}
                      formState={formState}
                      getValues={getValues}
                      directoryRoles={directoryRoles}
                    />
                  ) : null}
                </SharedActionAccordion>
              );
            })}
        </Stack>
      </Box>
    </Stack>
  );
}
